<div class="form-page">

  <!-- Breadcrumb ──────────────────────────────────────────────────────────── -->
  <div class="breadcrumb">
    <a routerLink="/plans" class="breadcrumb-link">
      <i class="pi pi-angle-left"></i>
      Plans &amp; Tarifs
    </a>
    <i class="pi pi-chevron-right breadcrumb-sep"></i>
    <span class="breadcrumb-current">{{ isEdit() ? 'Modifier le plan' : 'Nouveau plan' }}</span>
  </div>

  <h1 class="form-title">{{ isEdit() ? 'Modifier le plan' : 'Créer un nouveau plan' }}</h1>

  @if (loading()) {
    <div class="loading-wrap"><p-progressSpinner strokeWidth="4" styleClass="spinner-sm" /></div>
  } @else {

    <form [formGroup]="form" (ngSubmit)="save()" class="form-layout">

      <div class="form-main">

        @if (error()) {
          <div class="error-banner">
            <i class="pi pi-exclamation-circle"></i>
            {{ error() }}
          </div>
        }

        <!-- Section : Identité ─────────────────────────────────────────── -->
        <div class="form-section">
          <div class="section-head">
            <div class="section-icon section-icon--blue"><i class="pi pi-id-card"></i></div>
            <div>
              <h2 class="section-title">Identité du plan</h2>
              <p class="section-desc">Nom, code et positionnement</p>
            </div>
          </div>

          <div class="fields-row">
            <div class="field-wrap">
              <label class="field-label">Code <span class="req">*</span></label>
              <input pInputText formControlName="code" placeholder="ex: FREE, PRO, PREMIUM" class="full-field" />
              @if (form.get('code')?.hasError('required')) {
                <p class="field-error">Le code est requis</p>
              }
              <p class="field-hint">Identifiant unique, non modifiable après création</p>
            </div>
            <div class="field-wrap">
              <label class="field-label">Nom du plan <span class="req">*</span></label>
              <input pInputText formControlName="name" placeholder="ex: Professionnel" class="full-field" />
              @if (form.get('name')?.hasError('required')) {
                <p class="field-error">Le nom est requis</p>
              }
            </div>
          </div>

          <div class="toggle-row" [class.toggle-row--on]="form.get('featured')?.value">
            <div class="toggle-info">
              <i class="pi pi-star-fill"></i>
              <div>
                <p class="toggle-label">Plan populaire</p>
                <p class="toggle-desc">Mis en avant sur la page de tarification</p>
              </div>
            </div>
            <p-toggleswitch formControlName="featured" />
          </div>
        </div>

        <!-- Section : Tarification ─────────────────────────────────────── -->
        <div class="form-section">
          <div class="section-head">
            <div class="section-icon section-icon--orange"><i class="pi pi-money-bill"></i></div>
            <div>
              <h2 class="section-title">Tarification</h2>
              <p class="section-desc">Prix et durée de validité</p>
            </div>
          </div>

          <div class="fields-row">
            <div class="field-wrap">
              <label class="field-label">Prix mensuel <span class="req">*</span></label>
              <p-inputNumber formControlName="priceMonthly" [min]="0" suffix=" FCFA" class="full-field" />
              <p class="field-hint">Mettre 0 pour un plan gratuit</p>
            </div>
            <div class="field-wrap">
              <label class="field-label">Durée de validité <span class="req">*</span></label>
              <p-inputNumber formControlName="validityDays" [min]="1" suffix=" jours" class="full-field" />
            </div>
          </div>
        </div>

        <!-- Section : Limites ──────────────────────────────────────────── -->
        <div class="form-section">
          <div class="section-head">
            <div class="section-icon section-icon--green"><i class="pi pi-sliders-h"></i></div>
            <div>
              <h2 class="section-title">Limites d'utilisation</h2>
              <p class="section-desc">Capacités accordées à l'abonné</p>
            </div>
          </div>

          <div class="fields-row">
            <div class="field-wrap">
              <label class="field-label">Biens max <span class="req">*</span></label>
              <p-inputNumber formControlName="maxProperties" class="full-field" />
              <p class="field-hint">-1 = illimité</p>
            </div>
            <div class="field-wrap">
              <label class="field-label">Photos max par bien <span class="req">*</span></label>
              <p-inputNumber formControlName="maxPhotos" class="full-field" />
              <p class="field-hint">-1 = illimité</p>
            </div>
          </div>

          <div class="fields-row">
            <div class="field-wrap">
              <label class="field-label">Vidéos max par bien <span class="req">*</span></label>
              <p-inputNumber formControlName="maxVideos" class="full-field" />
              <p class="field-hint">-1 = illimité</p>
            </div>
            <div class="field-wrap">
              <label class="field-label">Panoramas max (visite virtuelle) <span class="req">*</span></label>
              <p-inputNumber formControlName="maxPanoramas" [disabled]="!form.get('virtualTourEnabled')?.value" class="full-field" />
              <p class="field-hint">-1 = illimité</p>
            </div>
          </div>

          <div class="toggle-row" [class.toggle-row--on]="form.get('virtualTourEnabled')?.value">
            <div class="toggle-info">
              <i class="pi pi-verified"></i>
              <div>
                <p class="toggle-label">Visite virtuelle 360°</p>
                <p class="toggle-desc">Autorise l'ajout de panoramas sur les biens de ce plan</p>
              </div>
            </div>
            <p-toggleswitch formControlName="virtualTourEnabled" />
          </div>
        </div>

        <!-- Section : Fonctionnalités ──────────────────────────────────── -->
        <div class="form-section">
          <div class="section-head">
            <div class="section-icon section-icon--purple"><i class="pi pi-list-check"></i></div>
            <div>
              <h2 class="section-title">Fonctionnalités incluses</h2>
              <p class="section-desc">Liste affichée sur la page de tarification</p>
            </div>
          </div>

          <div class="features-list" formArrayName="features">
            @for (ctrl of featuresArray.controls; track $index) {
              <div class="feature-row">
                <div class="feature-index">{{ $index + 1 }}</div>
                <span class="p-input-icon-left feature-field-wrap">
                  <i class="pi pi-check feat-prefix"></i>
                  <input pInputText [formControlName]="$index"
                         placeholder="ex: Annonces visibles 30 jours" class="feature-field" />
                </span>
                <button pButton type="button" [text]="true" severity="danger" class="feat-del"
                        (click)="removeFeature($index)"
                        [disabled]="featuresArray.length === 1"
                        icon="pi pi-times" pTooltip="Supprimer"></button>
              </div>
            }
          </div>

          <button pButton type="button" [outlined]="true" class="btn-add-feat"
                  (click)="addFeature()" icon="pi pi-plus" label="Ajouter une fonctionnalité"></button>
        </div>

      </div>

      <!-- Actions ────────────────────────────────────────────────────────── -->
      <div class="form-actions-bar">
        <a pButton [text]="true" routerLink="/plans" class="btn-cancel" label="Annuler"></a>
        <button pButton type="submit" class="btn-save"
                [disabled]="!form.valid || saving()" [loading]="saving()"
                [icon]="isEdit() ? 'pi pi-save' : 'pi pi-plus-circle'"
                [label]="isEdit() ? 'Mettre à jour' : 'Créer le plan'"></button>
      </div>

    </form>
  }
</div>
